import { SOCIAL_PLATFORM_LABELS, SOCIAL_PLATFORMS } from "@salon/types";
import { Controller, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import type { SocialLinksFormData } from "../hooks/use-social-links";
import { SocialPlatformIcon } from "./social-platform-icon";

interface SocialLinksCardProps {
  form: UseFormReturn<SocialLinksFormData>;
  loading: boolean;
  saving: boolean;
}

export function SocialLinksCard({ form, loading, saving }: SocialLinksCardProps) {
  const { t } = useTranslation("settings");

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("storefront.socialLinks.description")}</p>

      {SOCIAL_PLATFORMS.map((platform) => {
        const isWebsite = platform === "WEBSITE";
        const inputId = `social-link-${platform.toLowerCase()}`;
        return (
          <div key={platform} className="space-y-2">
            <label
              htmlFor={inputId}
              className="flex items-center gap-2 text-sm font-medium leading-none"
            >
              <SocialPlatformIcon platform={platform} className="h-4 w-4 text-muted-foreground" />
              {isWebsite
                ? t("storefront.socialLinks.websiteLabel")
                : SOCIAL_PLATFORM_LABELS[platform]}
            </label>
            {/* Controller, not `form.register`: React Compiler memoizes `register`, so it
                never re-runs after `form.reset` drops the field and the input stops tracking. */}
            <Controller
              control={form.control}
              name={platform}
              render={({ field }) => (
                <Input
                  id={inputId}
                  type="text"
                  placeholder={
                    isWebsite
                      ? t("storefront.socialLinks.websitePlaceholder")
                      : t("storefront.socialLinks.handlePlaceholder")
                  }
                  {...field}
                  disabled={loading || saving}
                />
              )}
            />
          </div>
        );
      })}
    </div>
  );
}
